import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ai } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title, Button, Group } from '@repletoryreach/sdk-ui';
import { Select, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../i18n/I18nProvider';
import { AiError, AiOutput, ModelSelect, PAGE_FORMAT } from './shared';

export const SKILLS_KEY = ['ai', 'skills'];

/** Inputs that take a paragraph rather than a line. */
const LONG = new Set(['text', 'topic', 'details', 'message', 'context']);

/**
 * Write (14 October 2026): run a skill, built in or the business's own. The inputs come from the skill itself, so a
 * skill the business saves shows up here with nothing else to build. "Write an article about the school festival in
 * a fun tone" is the write skill with a topic and a tone.
 */
export function WriteTab() {
  const t = useT();
  const skillsQ = useQuery({ queryKey: SKILLS_KEY, queryFn: () => ai.listSkills() });
  const skills = useMemo(() => skillsQ.data?.data ?? [], [skillsQ.data]);
  const [code, setCode] = useState('write');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [model, setModel] = useState('');
  const skill = skills.find((s) => s.code === code) ?? skills[0];

  useEffect(() => { setInputs({}); }, [code]);

  const run = useMutation({
    mutationFn: () => ai.runSkill({ skill: skill!.code, inputs, model: model || null, format: PAGE_FORMAT })
  });

  const name = (s: ai.AiSkill) => (s.built_in ? t(`ai.skill.${s.code}.name`, s.name) : s.name);
  const missing = skill?.inputs.filter((i) => i.required && !(inputs[i.name] ?? '').trim()) ?? [];

  return (
    <Stack gap="lg">
      <Card withBorder>
        <form onSubmit={(e) => { e.preventDefault(); if (skill && missing.length === 0) run.mutate(); }}>
          <Stack gap="md">
            <Title order={4}>{t('ai.write.title')}</Title>
            <Select
              label={t('ai.write.skill')}
              value={skill?.code ?? ''}
              onChange={(e) => setCode(e.currentTarget.value)}
              data={[
                ...skills.filter((s) => s.built_in).map((s) => ({ value: s.code, label: name(s) })),
                ...skills.filter((s) => !s.built_in).map((s) => ({ value: s.code, label: t('ai.write.own', { name: s.name }) }))
              ]}
              description={skill ? (skill.built_in ? t(`ai.skill.${skill.code}.description`, skill.description) : skill.description) : undefined}
            />
            {skill?.inputs.map((input) => {
              const label = t(`ai.input.${input.name}.label`, input.name);
              const hint = t(`ai.input.${input.name}.hint`, input.description);
              const value = inputs[input.name] ?? '';
              const set = (v: string) => setInputs((cur) => ({ ...cur, [input.name]: v }));
              if (input.choices?.length)
                return (
                  <Select key={input.name} label={label} description={hint} value={value}
                    onChange={(e) => set(e.currentTarget.value)}
                    data={[{ value: '', label: t('ai.input.default') }, ...input.choices.map((c) => ({ value: c, label: t(`ai.choice.${c}`, c) }))]} />
                );
              return LONG.has(input.name)
                ? <Textarea key={input.name} label={label} description={hint} value={value} required={input.required}
                    minRows={input.name === 'text' ? 6 : 3} autosize onChange={(e) => set(e.currentTarget.value)} />
                : <TextInput key={input.name} label={label} description={hint} value={value} required={input.required}
                    onChange={(e) => set(e.currentTarget.value)} />;
            })}
            <ModelSelect value={model} onChange={setModel} />
            <Group justify="flex-end">
              <Button type="submit" loading={run.isPending} disabled={!skill || missing.length > 0} data-testid="ai-write-run">
                {t('ai.write.run')}
              </Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.chargeNote')}</Text>
          </Stack>
        </form>
      </Card>
      <AiError error={run.error} />
      {run.data && <Card withBorder><AiOutput result={run.data.data} /></Card>}
    </Stack>
  );
}
